import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  CreditCard, Check, X, Gift, Lock, CheckCircle,
  ChevronDown, ChevronUp, Loader2, AlertCircle, Calendar
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

// Stripe will be loaded dynamically when needed
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get(`${backendUrl}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
      // Use test key as fallback
      stripePromise = loadStripe('pk_test_placeholder');
    }
  }
  return stripePromise;
};

// Card Element styling - Dark theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#f1f5f9',
      backgroundColor: 'transparent',
      '::placeholder': {
        color: '#64748b',
      },
    },
    invalid: {
      color: '#f87171',
    },
  },
};

// Payment Form Component
const PaymentForm = ({ selectedPlan, selectedTier, billingPeriod, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        return;
      }

      // Create subscription with payment method
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/user-subscriptions`,
        {
          plan_id: selectedPlan.id,
          pricing_tier_id: selectedTier.tier_id,
          payment_method_id: paymentMethod.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onSuccess(response.data.subscription);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const price = selectedTier?.price || selectedTier?.pricing || 0;
  const period = billingPeriod || selectedTier?.period || 'monthly';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Plan Summary */}
      <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="text-base font-semibold text-white">{selectedPlan?.title}</h4>
            <p className="text-sm text-slate-400">{selectedTier?.plan_name || (period === 'monthly' ? 'Monthly' : 'Yearly')} Plan</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-white">${parseFloat(price).toFixed(2)}</p>
            <p className="text-sm text-slate-400">per {period === 'yearly' ? 'year' : 'month'}</p>
          </div>
        </div>
        {selectedPlan?.free_trial_enabled && (
          <div className="flex items-center gap-1 text-emerald-400 text-sm mt-2">
            <Gift className="h-4 w-4" />
            <span>{selectedPlan.free_trial_days}-day free trial included</span>
          </div>
        )}
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-base font-medium text-slate-200 mb-2">
          Card Details
        </label>
        <div className="border border-slate-600 rounded-lg p-4 bg-slate-700/50">
          <CardElement 
            options={cardElementOptions}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 text-red-400 text-sm p-3 rounded-lg flex items-center gap-2 border border-red-500/30">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Terms */}
      <p className="text-sm text-slate-400">
        By subscribing, you agree to automatic {period} billing of ${parseFloat(price).toFixed(2)}. 
        You can cancel anytime from your subscription settings.
      </p>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
        <Lock className="h-4 w-4" />
        <span>Secure payment powered by Stripe</span>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 px-4 py-3 border border-slate-600 rounded-lg text-base text-slate-300 hover:bg-slate-700 font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={processing || !stripe || !cardComplete}
          className="flex-1 px-4 py-3 bg-[#29add6] text-white rounded-lg text-base font-medium hover:bg-[#1e9bc4] disabled:bg-slate-600 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          {processing ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="h-5 w-5" />
              Pay ${parseFloat(price).toFixed(2)}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const SubscriptionPricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedSubscription, setCompletedSubscription] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedColumnId, setSelectedColumnId] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchMySubscriptions();
    }
    // Initialize Stripe
    getStripePromise().then((stripe) => {
      if (stripe) setStripeReady(true);
    });
  }, [user]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${backendUrl}/api/subscription-plans`, { headers });
      
      if (response.data.success) {
        const enabledPlans = response.data.plans.filter(p => p.is_enabled);
        setPlans(enabledPlans);
        
        // Set default selected column to "most_popular" if exists
        for (const plan of enabledPlans) {
          if (plan.comparison_columns) {
            const popularColumn = plan.comparison_columns.find(col => col.badge === 'most_popular');
            if (popularColumn) {
              setSelectedColumnId(popularColumn.column_id);
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      showNotification('Failed to load subscription plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${backendUrl}/api/user-subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMySubscriptions(response.data.subscriptions.filter(s => 
          s.status === 'active' || s.status === 'trialing'
        ));
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const handleSelectPlan = (plan, tier = null) => {
    if (!user) {
      showNotification('Please log in to subscribe', 'error');
      return;
    }
    
    setSelectedPlan(plan);
    const selectedTierData = tier || plan.pricing_tiers?.find(t => t.period === billingPeriod) || plan.pricing_tiers?.[0];
    setSelectedTier(selectedTierData);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (subscription) => {
    setShowPaymentModal(false);
    setCompletedSubscription(subscription);
    setShowSuccessModal(true);
    fetchMySubscriptions();
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setCompletedSubscription(null);
    setSelectedPlan(null);
    setSelectedTier(null);
    navigate('/');
  };

  const formatBadgeLabel = (badge) => {
    if (!badge || badge === 'none') return null;
    const badgeLabels = {
      'best_value': 'Best Value',
      'most_popular': 'Most Popular',
      'recommended': 'Recommended',
      'new': 'New',
      'limited': 'Limited Time',
      'premium': 'Premium'
    };
    return badgeLabels[badge] || badge.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const calculateYearlyPrice = (monthlyPrice, yearlyDiscount, yearlyPrice) => {
    if (yearlyPrice && parseFloat(yearlyPrice) > 0) {
      return parseFloat(yearlyPrice);
    }
    if (yearlyDiscount && parseFloat(yearlyDiscount) > 0) {
      const annualTotal = parseFloat(monthlyPrice) * 12;
      return annualTotal * (1 - parseFloat(yearlyDiscount) / 100);
    }
    return parseFloat(monthlyPrice) * 12;
  };

  // Render comparison plan table
  const renderComparisonPlan = (plan) => {
    if (!plan.comparison_columns || plan.comparison_columns.length === 0) {
      return null;
    }

    return (
      <div key={plan.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 w-1/4">
                  Plan Cost
                </th>
                {plan.comparison_columns.map((col, idx) => {
                  const monthlyPrice = parseFloat(col.pricing || col.price || 0);
                  const yearlyPriceValue = parseFloat(col.yearly_pricing || 0);
                  const yearlyDiscount = parseFloat(col.yearly_discount || 0);
                  
                  const displayPrice = billingPeriod === 'yearly' 
                    ? (yearlyPriceValue > 0 ? yearlyPriceValue : calculateYearlyPrice(monthlyPrice, yearlyDiscount, yearlyPriceValue))
                    : monthlyPrice;
                  
                  const originalYearlyPrice = monthlyPrice * 12;
                  const hasYearlyDiscount = billingPeriod === 'yearly' && (yearlyPriceValue > 0 || yearlyDiscount > 0) && displayPrice < originalYearlyPrice;
                  const badgeLabel = formatBadgeLabel(col.badge);
                  const isSelected = selectedColumnId === col.column_id;
                  
                  return (
                  <th 
                    key={col.column_id || idx} 
                    className={`px-3 py-3 text-center cursor-pointer transition-colors ${isSelected ? 'bg-[#29add6]/10' : ''}`}
                    onClick={() => setSelectedColumnId(col.column_id)}
                  >
                    <div className="space-y-1">
                      {badgeLabel && (
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${
                          col.badge === 'most_popular' ? 'bg-[#29add6] text-white' :
                          col.badge === 'best_value' ? 'bg-green-500 text-white' :
                          'bg-orange-500 text-white'
                        }`}>
                          {badgeLabel}
                        </span>
                      )}
                      <h3 className="text-base font-semibold text-gray-900">{col.plan_name || col.title}</h3>
                      <div>
                        {hasYearlyDiscount && (
                          <span className="text-sm text-gray-400 line-through mr-1">
                            ${originalYearlyPrice.toFixed(0)}
                          </span>
                        )}
                        <span className="text-xl font-bold text-gray-900">${displayPrice.toFixed(0)}</span>
                        <span className="text-sm text-gray-500">/{billingPeriod === 'yearly' ? 'yr' : 'mo'}</span>
                        {hasYearlyDiscount && yearlyDiscount > 0 && (
                          <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">
                            -{yearlyDiscount}%
                          </span>
                        )}
                      </div>
                      {billingPeriod === 'yearly' && (
                        <p className="text-xs text-gray-500">${(displayPrice / 12).toFixed(2)}/mo</p>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedColumnId(col.column_id);
                          handleSelectPlan(plan, {
                            tier_id: col.column_id,
                            price: displayPrice,
                            period: billingPeriod
                          });
                        }}
                        className={`w-full py-2 px-3 rounded text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-[#29add6] text-white hover:bg-[#1e9bc4]'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {col.button_text || 'Subscribe'}
                      </button>
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {plan.sections?.map((section, sIdx) => (
                <React.Fragment key={section.section_id || sIdx}>
                  {section.title && (
                    <tr className="bg-gray-50">
                      <td colSpan={1 + (plan.comparison_columns?.length || 0)} className="px-4 py-2">
                        <span className="text-sm font-semibold text-gray-700">{section.title}</span>
                      </td>
                    </tr>
                  )}
                  {section.line_items?.map((item, iIdx) => {
                    const itemText = typeof item === 'string' ? item : (item.text || item.name);
                    const itemId = item.item_id || item.id;
                    
                    return (
                      <tr key={itemId || iIdx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-700">{itemText}</td>
                        {plan.comparison_columns?.map((col, cIdx) => {
                          const isAvailable = col.feature_availability?.[itemId] === 'available';
                          const customText = col.feature_additional_text?.[itemId];
                          const colValue = item.column_values?.[cIdx];
                          const isSelected = selectedColumnId === col.column_id;
                          
                          return (
                            <td key={cIdx} className={`px-3 py-2 text-center transition-colors ${isSelected ? 'bg-[#29add6]/5' : ''}`}>
                              {customText && customText.trim() !== '' ? (
                                <span className="text-sm text-gray-900 font-medium">{customText}</span>
                              ) : isAvailable ? (
                                <Check className="h-5 w-5 text-green-500 mx-auto" />
                              ) : colValue === true || colValue === 'true' || colValue === '✓' ? (
                                <Check className="h-5 w-5 text-green-500 mx-auto" />
                              ) : colValue && colValue !== 'false' && colValue !== '✗' ? (
                                <span className="text-sm text-gray-900 font-medium">{colValue}</span>
                              ) : (
                                <X className="h-5 w-5 text-gray-300 mx-auto" />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render single plan as table (similar to comparison)
  const renderSinglePlanTable = (plan) => {
    const tiers = plan.pricing_tiers || [];
    
    if (tiers.length === 0) {
      return null;
    }

    return (
      <div key={plan.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 w-1/2">
                  Plan Cost
                </th>
                {tiers.map((tier, idx) => {
                  const monthlyPrice = parseFloat(tier.price || 0);
                  const yearlyPriceValue = parseFloat(tier.yearly_price || 0);
                  const yearlyDiscount = parseFloat(tier.yearly_discount || 0);
                  
                  const displayPrice = billingPeriod === 'yearly' 
                    ? (yearlyPriceValue > 0 ? yearlyPriceValue : calculateYearlyPrice(monthlyPrice, yearlyDiscount, yearlyPriceValue))
                    : monthlyPrice;
                  
                  const originalYearlyPrice = monthlyPrice * 12;
                  const hasYearlyDiscount = billingPeriod === 'yearly' && (yearlyPriceValue > 0 || yearlyDiscount > 0) && displayPrice < originalYearlyPrice;
                  
                  return (
                    <th key={tier.tier_id || idx} className="px-3 py-3 text-center">
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-gray-900">
                          {tier.period === 'monthly' ? 'Monthly' : 'Yearly'} Plan
                        </h3>
                        <div>
                          {hasYearlyDiscount && (
                            <span className="text-sm text-gray-400 line-through mr-1">
                              ${originalYearlyPrice.toFixed(0)}
                            </span>
                          )}
                          <span className="text-xl font-bold text-gray-900">${displayPrice.toFixed(0)}</span>
                          <span className="text-sm text-gray-500">/{billingPeriod === 'yearly' ? 'yr' : 'mo'}</span>
                          {hasYearlyDiscount && yearlyDiscount > 0 && (
                            <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">
                              -{yearlyDiscount}%
                            </span>
                          )}
                        </div>
                        {billingPeriod === 'yearly' && (
                          <p className="text-xs text-gray-500">${(displayPrice / 12).toFixed(2)}/mo</p>
                        )}
                        {plan.free_trial_enabled && (
                          <p className="text-xs text-green-600">
                            <Gift className="h-3 w-3 inline mr-0.5" />
                            {plan.free_trial_days} days free
                          </p>
                        )}
                        <button
                          onClick={() => handleSelectPlan(plan, {
                            tier_id: tier.tier_id,
                            price: displayPrice,
                            period: billingPeriod
                          })}
                          className="w-full py-2 px-3 rounded text-sm font-medium transition-all bg-[#29add6] text-white hover:bg-[#1e9bc4]"
                        >
                          Subscribe
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {plan.sections?.map((section, sIdx) => (
                <React.Fragment key={section.section_id || sIdx}>
                  {section.title && (
                    <tr className="bg-gray-50">
                      <td colSpan={1 + tiers.length} className="px-4 py-2">
                        <span className="text-sm font-semibold text-gray-700">{section.title}</span>
                      </td>
                    </tr>
                  )}
                  {section.line_items?.map((item, iIdx) => {
                    const itemText = typeof item === 'string' ? item : (item.text || item.name);
                    const isAvailable = typeof item === 'string' ? true : (item.availability !== 'unavailable');
                    const additionalInfo = typeof item === 'string' ? null : item.additional_info;
                    
                    return (
                      <tr key={iIdx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-700">{itemText}</td>
                        {tiers.map((tier, tIdx) => (
                          <td key={tIdx} className="px-3 py-2 text-center">
                            {additionalInfo && additionalInfo.trim() !== '' ? (
                              <span className="text-sm text-gray-900 font-medium">{additionalInfo}</span>
                            ) : isAvailable ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#29add6]" />
            <span className="ml-2 text-gray-600 text-sm">Loading plans...</span>
          </div>
        </div>
      </div>
    );
  }

  const comparisonPlans = plans.filter(p => p.plan_type === 'comparison');
  const singlePlans = plans.filter(p => p.plan_type !== 'comparison');
  const allTabbedPlans = [...comparisonPlans, ...singlePlans];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-3 py-2 rounded-lg shadow-lg text-sm ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white flex items-center gap-2`}>
            {notification.type === 'error' && <AlertCircle className="h-4 w-4" />}
            {notification.message}
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">Subscription Plans</h1>
          </div>

          {/* Plan Tabs with Billing Toggle */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between px-4">
              {/* Tabs */}
              <nav className="flex -mb-px">
                {allTabbedPlans.map((plan, idx) => (
                  <button
                    key={plan.id}
                    onClick={() => setActiveTab(idx)}
                    className={`py-3 px-4 text-sm font-medium transition-colors ${
                      activeTab === idx
                        ? 'bg-[#29add6] text-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {plan.title}
                  </button>
                ))}
              </nav>
              
              {/* Billing Period Toggle */}
              <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    billingPeriod === 'monthly' ? 'bg-[#29add6] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('yearly')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    billingPeriod === 'yearly' ? 'bg-[#29add6] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>
          </div>

          {/* Plan Content */}
          <div className="p-4">
            {allTabbedPlans.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No plans available</p>
              </div>
            ) : (
              <>
                {allTabbedPlans.map((plan, idx) => (
                  <div key={plan.id} className={activeTab === idx ? 'block' : 'hidden'}>
                    {plan.plan_type === 'comparison' ? (
                      renderComparisonPlan(plan)
                    ) : (
                      renderSinglePlanTable(plan)
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* My Subscriptions */}
        {mySubscriptions.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm mt-4">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Your Active Subscriptions</h2>
            </div>
            <div className="p-4">
              {mySubscriptions.map((sub) => (
                <div key={sub.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{sub.plan_title}</p>
                    <p className="text-xs text-gray-500">
                      Renews {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">${sub.price}/{sub.billing_period === 'monthly' ? 'mo' : 'yr'}</p>
                    <span className="text-xs text-green-600">Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && selectedTier && stripeReady && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-700">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Complete Your Subscription</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPlan(null);
                  setSelectedTier(null);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-5">
              <Elements stripe={stripePromise}>
                <PaymentForm
                  selectedPlan={selectedPlan}
                  selectedTier={selectedTier}
                  billingPeriod={billingPeriod}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => {
                    setShowPaymentModal(false);
                    setSelectedPlan(null);
                    setSelectedTier(null);
                  }}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {/* Success Confirmation Modal */}
      {showSuccessModal && completedSubscription && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-700">
            {/* Success Header - Compact */}
            <div className="p-5 text-center border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white">Subscription Confirmed!</h3>
              <p className="text-slate-400 mt-1">Thank you for your purchase</p>
            </div>
            
            {/* Subscription Details */}
            <div className="p-6 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-3 border border-slate-600">
                <div className="flex justify-between">
                  <span className="text-base text-slate-400">Plan</span>
                  <span className="text-base font-semibold text-white">{completedSubscription.plan_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-base text-slate-400">Amount</span>
                  <span className="text-base font-semibold text-white">
                    ${completedSubscription.price}/{completedSubscription.billing_period === 'yearly' ? 'yr' : 'mo'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-base text-slate-400">Billing Cycle</span>
                  <span className="text-base font-semibold text-white capitalize">{completedSubscription.billing_period}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-base text-slate-400">Status</span>
                  <span className="text-base font-semibold text-emerald-400 capitalize">{completedSubscription.status}</span>
                </div>
                {completedSubscription.current_period_end && (
                  <div className="flex justify-between">
                    <span className="text-base text-slate-400">Next Billing Date</span>
                    <span className="text-base font-semibold text-white">
                      {new Date(completedSubscription.current_period_end).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Payment Terms */}
              <div className="border border-slate-600 rounded-lg p-4 bg-slate-700/30">
                <h4 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  Payment Terms
                </h4>
                <ul className="text-sm text-slate-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Your card will be charged ${completedSubscription.price} every {completedSubscription.billing_period === 'yearly' ? 'year' : 'month'}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    You can cancel your subscription anytime
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    Cancellation takes effect at the end of your billing period
                  </li>
                </ul>
              </div>
            </div>

            {/* Close Button */}
            <div className="p-5 border-t border-slate-700">
              <button
                onClick={handleCloseSuccess}
                className="w-full px-4 py-3 bg-[#29add6] text-white rounded-lg text-base font-medium hover:bg-[#1e9bc4] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPricing;
